import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Stack, TextField, Typography} from 'views/components';

import {AdminAutocomplete} from '../autcomplete';
import {Translate} from '../Translate';

const DraftMessage = (props) => {
  const {
    actions = [],
    bodyField,
    fields = [],
  } = props;

  // contentEditable needs a DOM ref so we can sync external HTML updates without resetting the cursor on each keystroke.
  const htmlBodyEditorRef = React.useRef(null);

  const bodyMirrorValue = `${bodyField.value ?? ' '}\u200b`;

  React.useEffect(() => {
    if (!bodyField.isHtml || !htmlBodyEditorRef.current) {
      return;
    }

    if (htmlBodyEditorRef.current.innerHTML !== (bodyField.value ?? '')) {
      htmlBodyEditorRef.current.innerHTML = bodyField.value ?? '';
    }
  }, [bodyField.isHtml, bodyField.value]);

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #CDD7FF',
        borderRadius: '16px',
        boxShadow: '0px 1px 2px rgba(130, 145, 255, 0.06)',
        overflow: 'visible',
      }}
    >
      <Stack
        sx={{
          px: '18px',
          py: '12px',
          gap: '4px',
        }}
      >
        {
          fields.length > 0 && (
            <Stack
              sx={{
                gap: '8px',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                }}
              >
                {
                  fields.map((field) => {
                    if (field.type === 'readonly') {
                      return (
                        <Typography
                          key={field.key}
                          sx={{
                            fontSize: '11px',
                            fontWeight: 400,
                            lineHeight: '150%',
                            color: '#A8A49E',
                            ...field.sx,
                          }}
                        >
                          <Translate expression={field.expression} />: {field.value}
                        </Typography>
                      );
                    }

                    return (
                      <Stack
                        key={field.key}
                        direction='row'
                        alignItems='flex-start'
                        sx={{
                          gap: '4px',
                          ...field.rowSx,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '11px',
                            fontWeight: 400,
                            lineHeight: '150%',
                            color: '#A8A49E',
                          }}
                        >
                          <Translate expression={field.expression} />:
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            pt: '1px',
                            position: 'relative',
                          }}
                        >
                          {
                            field.type === 'adminAutocomplete' &&
                              <AdminAutocomplete
                                selectedAdmin={field.selectedAdmin}
                                selectedDisplayValue={field.selectedDisplayValue}
                                setSelectedAdmin={field.setSelectedAdmin}
                                variant='standard'
                                showEmail={field.showEmail}
                                autocompleteSx={{
                                  width: '100%',
                                  ...field.autocompleteSx,
                                }}
                                textFieldSx={{
                                  'minWidth': '100%',
                                  '& .MuiAutocomplete-inputRoot': {
                                    alignItems: 'center',
                                  },
                                  '& .MuiAutocomplete-input': {
                                    p: '0 !important',
                                    fontSize: '11px',
                                    fontWeight: 400,
                                    lineHeight: '150%',
                                    color: '#A8A49E',
                                  },
                                  '& .MuiInputBase-root': {
                                    height: 'inherit',
                                    py: 0,
                                  },
                                  '& .MuiInputBase-input': {
                                    py: 0,
                                    fontSize: '11px',
                                    fontWeight: 400,
                                    lineHeight: '150%',
                                    color: '#A8A49E',
                                  },
                                  '& .MuiInput-underline:before': {
                                    borderBottom: '0',
                                  },
                                  '& .MuiInput-underline:after': {
                                    borderBottom: '0',
                                  },
                                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                    borderBottom: '0',
                                  },
                                  ...field.inputSx,
                                }}
                              />
                          }
                          {
                            field.type !== 'adminAutocomplete' &&
                              <TextField
                                fullWidth
                                variant='standard'
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                sx={{
                                  '& .MuiInputBase-input': {
                                    py: 0,
                                    fontSize: '11px',
                                    fontWeight: 400,
                                    lineHeight: '150%',
                                    color: '#A8A49E',
                                  },
                                  '& .MuiInput-underline:before': {
                                    borderBottom: '0',
                                  },
                                  '& .MuiInput-underline:after': {
                                    borderBottom: '0',
                                  },
                                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                    borderBottom: '0',
                                  },
                                  ...field.inputSx,
                                }}
                              />
                          }
                        </Box>
                      </Stack>
                    );
                  })
                }
              </Box>
            </Stack>
          )
        }
        <Box
          sx={{
            backgroundColor: '#FBFAF8',
            borderRadius: '8px',
            p: '12px',
            ...bodyField.containerSx,
          }}
        >
          {
            bodyField.isHtml &&
              <Box
                key='html-body'
                id={bodyField.id}
                ref={htmlBodyEditorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={(event) => {
                  bodyField.onChange(event.currentTarget.innerHTML);
                }}
                sx={{
                  'minHeight': '1.8em',
                  'outline': 0,
                  'color': '#6F6A62',
                  'fontFamily': 'inherit',
                  'fontSize': '13px',
                  'fontWeight': 400,
                  'lineHeight': '1.8',
                  'whiteSpace': 'normal',
                  'overflowWrap': 'anywhere',
                  'wordBreak': 'break-word',
                  '& .gmail_quote_container': {
                    color: '#6F6A62',
                  },
                }}
              />
          }
          {
            !bodyField.isHtml &&
              <Box
                key='plain-body'
                sx={{
                  position: 'relative',
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    minHeight: '1.8em',
                    visibility: 'hidden',
                    fontFamily: 'inherit',
                    fontSize: '13px',
                    fontWeight: 400,
                    lineHeight: '1.8',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    boxSizing: 'border-box',
                  }}
                >
                  {bodyMirrorValue}
                </Box>
                <Box
                  component='textarea'
                  id={bodyField.id}
                  rows={1}
                  value={bodyField.value ?? ''}
                  onChange={(event) => {
                    bodyField.onChange(event.target.value);
                  }}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    p: 0,
                    border: 0,
                    outline: 0,
                    resize: 'none',
                    overflow: 'hidden',
                    backgroundColor: 'transparent',
                    color: '#6F6A62',
                    fontFamily: 'inherit',
                    fontSize: '13px',
                    fontWeight: 400,
                    lineHeight: '1.8',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    boxSizing: 'border-box',
                  }}
                />
              </Box>
          }
        </Box>
        <Stack
          direction='row'
          sx={{
            gap: '10px',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            mt: '8px',
          }}
        >
          {
            actions.map((action) => {
              if (action.content) {
                return (
                  <Box key={action.key} sx={action.sx}>
                    {action.content}
                  </Box>
                );
              }

              return (
                <Button
                  key={action.key}
                  variant={
                    action.variant === 'secondary' ? 'outlined' :
                    action.variant === 'tertiary' ? 'text' :
                    undefined
                  }
                  disableElevation={action.variant === 'primary'}
                  disabled={action.disabled}
                  onClick={action.onClick}
                  sx={{
                    ...(action.variant === 'primary' && {
                      'px': '10px',
                      'py': '8px',
                      'borderRadius': '7px',
                      'fontSize': '11px',
                      'fontWeight': 400,
                      'lineHeight': '100%',
                      'textTransform': 'none',
                      'color': '#FFFFFF',
                      'backgroundColor': '#E4875B',
                      '&:hover': {
                        backgroundColor: '#DB7D51',
                      },
                    }),
                    ...(action.variant === 'secondary' && {
                      'height': '42px',
                      'textTransform': 'none',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }),
                    ...(action.variant === 'tertiary' && {
                      'px': '4px',
                      'py': '10px',
                      'borderRadius': '7px',
                      'fontSize': '11px',
                      'fontWeight': 400,
                      'lineHeight': '100%',
                      'textTransform': 'none',
                      'color': '#6F6A62',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: '#1A1916',
                      },
                    }),
                    ...action.sx,
                  }}
                >
                  <Translate expression={action.expression} />
                </Button>
              );
            })
          }
        </Stack>
      </Stack>
    </Box>
  );
};

DraftMessage.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    content: PropTypes.node,
    key: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    expression: PropTypes.string,
    onClick: PropTypes.func,
    sx: PropTypes.object,
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
  })),
  bodyField: PropTypes.shape({
    id: PropTypes.string,
    isHtml: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string,
    containerSx: PropTypes.object,
  }).isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({
    autocompleteSx: PropTypes.object,
    expression: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    rowSx: PropTypes.object,
    selectedAdmin: PropTypes.object,
    selectedDisplayValue: PropTypes.string,
    setSelectedAdmin: PropTypes.func,
    showEmail: PropTypes.bool,
    sx: PropTypes.object,
    inputSx: PropTypes.object,
    type: PropTypes.oneOf(['readonly', 'editable', 'adminAutocomplete']).isRequired,
    value: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  })),
};

export default React.memo(DraftMessage);
